import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, AlertCircle, Save } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

export default function ReportSelection() {
    const [mode, setMode] = useState('single'); // 'single' | 'compare'
    const [tables, setTables] = useState([]);
    const [selection, setSelection] = useState({ t1: '', t2: '' });
    const [saved, setSaved] = useState(false);
    const [loading, setLoading] = useState(false);

    // Fetch tables on mount
    useEffect(() => {
        fetchTables();

        // Check if there is a saved context in localStorage
        const savedContext = localStorage.getItem('lorams_context');
        if (savedContext) {
            try {
                const ctx = JSON.parse(savedContext);
                setMode(ctx.mode);
                setSelection({ t1: ctx.tables[0] || '', t2: ctx.tables[1] || '' });
                setSaved(true);
            } catch (e) { }
        }
    }, []);

    // Warn on navigation if not saved (Browser level)
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (!saved && (selection.t1 || selection.t2)) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [saved, selection]);

    const fetchTables = async () => {
        try {
            const res = await axios.get(`${API}/tables`);
            setTables(res.data);
        } catch (error) {
            toast.error('Eroare la incarcarea listei de tabele');
        }
    };

    const handleSave = async () => {
        if (mode === 'single' && !selection.t1) {
            toast.error('Selecteaza o tabela!');
            return;
        }
        if (mode === 'compare' && (!selection.t1 || !selection.t2)) {
            toast.error('Selecteaza ambele tabele!');
            return;
        }

        setLoading(true);
        const tablesToSend = mode === 'single' ? [selection.t1] : [selection.t1, selection.t2];

        try {
            await axios.post(`${API}/validate_context`, tablesToSend);

            // Save to LocalStorage to act as "Session"
            const context = { mode, tables: tablesToSend };
            localStorage.setItem('lorams_context', JSON.stringify(context));

            setSaved(true);
            toast.success('Configuratia a fost salvata! Poti merge la Rapoarte.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la validarea selectiei');
            setSaved(false);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (key, val) => {
        setSelection(prev => ({ ...prev, [key]: val }));
        setSaved(false);
    };

    return (
        <div className="p-8">
            <div className="mb-8">
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Configurare Rapoarte
                </h1>
                <p className="text-gray-400">Alege sursele de date pentru analiza</p>
            </div>

            <div className="max-w-2xl mx-auto space-y-6">
                {/* Mode Selection */}
                <Card className="bg-[#18181b] border-white/10">
                    <CardHeader>
                        <CardTitle className="text-white">1. Alege Modul de Lucru</CardTitle>
                        <CardDescription className="text-gray-400">
                            Doresti un raport pe un singur fisier sau o comparatie intre doua perioade?
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Select value={mode} onValueChange={(val) => { setMode(val); setSaved(false); }}>
                            <SelectTrigger className="w-full bg-[#27272a] border-white/20 text-white">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#27272a] border-white/20 text-white">
                                <SelectItem value="single">Raport Fisier Unic</SelectItem>
                                <SelectItem value="compare">Comparatie (2 Fisiere)</SelectItem>
                            </SelectContent>
                        </Select>
                    </CardContent>
                </Card>

                {/* Table Selection */}
                <Card className="bg-[#18181b] border-white/10">
                    <CardHeader>
                        <CardTitle className="text-white">2. Selecteaza Sursele de Date</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm text-gray-400">Sursa Principala (Tabela A)</label>
                            <Select value={selection.t1} onValueChange={(val) => handleChange('t1', val)}>
                                <SelectTrigger className="w-full bg-[#27272a] border-white/20 text-white">
                                    <SelectValue placeholder="Selecteaza tabela..." />
                                </SelectTrigger>
                                <SelectContent className="bg-[#27272a] border-white/20 text-white max-h-[200px]">
                                    {tables.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>

                        {mode === 'compare' && (
                            <div className="space-y-2 pt-2 border-t border-white/5">
                                <label className="text-sm text-gray-400">Sursa de Comparatie (Tabela B)</label>
                                <Select value={selection.t2} onValueChange={(val) => handleChange('t2', val)}>
                                    <SelectTrigger className="w-full bg-[#27272a] border-white/20 text-white">
                                        <SelectValue placeholder="Selecteaza tabela..." />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#27272a] border-white/20 text-white max-h-[200px]">
                                        {tables.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Action */}
                <div className="flex items-center gap-4">
                    <Button
                        onClick={handleSave}
                        disabled={loading}
                        className={`flex-1 h-12 text-lg ${saved ? 'bg-green-600 hover:bg-green-700' : 'bg-cyan-500 hover:bg-cyan-600'}`}
                    >
                        {saved ? (
                            <><CheckCircle className="mr-2" /> Context Salvat</>
                        ) : (
                            <><Save className="mr-2" /> Salveaza Contextul</>
                        )}
                    </Button>

                    {!saved && (selection.t1 || selection.t2) && (
                        <div className="flex items-center text-orange-400 text-sm animate-pulse">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            Modificari nesalvate
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
